import { useMemo, useState } from "react";
import { Row, Col, Table, Alert, message, Button } from "antd";
import { ColumnsType } from "antd/lib/table";
import API, { apis, AUTH_HEADER_NAME } from "common/apis";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { getWhDivisionId } from "services/PersistService/PersistDivisionService";

const MESSAGE_KEY = "upload_onboarding_details";

// Sample / template file — matches the "mysa_gig_worker_onboarding" sheet format.
const SAMPLE_CSV = [
  "S.No,Name of Vendor,Pan Number,Address,State,Bank Name,A/c Holder Name,A/c Number,IFSC Code",
  "1,GIG-9001-RAHUL SHARMA,ABCPS1234K,12 MG Road Andheri East,Maharashtra,State Bank of India,rahul sharma,44592741286,SBIN0011234",
  "2,GIG-9002-PRIYA VERMA,BXYPV5678L,45 Park Street,West Bengal,HDFC Bank,priya verma,41433959855,HDFC0004567",
].join("\n");

// Fallback column labels, matching the upload API response rows.
const DEFAULT_HEADERS: Record<string, string> = {
  userId: "User ID",
  name: "Name",
  mysaPartnerId: "MYSA Partner ID",
  currentStatus: "Current Status",
  alreadyCreated: "Already Created",
};

interface IOnboardingUploadRow {
  errors?: { message: string }[];
  [key: string]: any;
}

interface IOnboardingUploadResult {
  key?: string | null;
  total?: number;
  success?: number;
  skipped?: number;
  failed?: number;
  headerMapping?: string;
  successRows?: IOnboardingUploadRow[];
  failedRows?: IOnboardingUploadRow[];
  [key: string]: any;
}

const UploadOnboardingDetailsPage = () => {
  useDocumentTitle("Upload Onboarding Details");
  const { authToken } = useAppSelector((state) => state.app.user);
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadResult, setUploadResult] = useState<IOnboardingUploadResult>({});

  const whDivId = getWhDivisionId();

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
    ...(whDivId && { "wh-div-id": whDivId.toString() }),
  };

  const onUploading = () => {
    setHasErrorRows(false);
    setUploadResult({});
  };

  const onUploaded = (data: IOnboardingUploadResult) => {
    setUploadResult(data || {});
    setHasErrorRows(!!data?.failedRows?.length || !!data?.failed);
  };

  const successRows = uploadResult?.successRows || [];
  const failedRows = uploadResult?.failedRows || [];
  const successData = successRows.map((row, i) => ({ ...row, key: `data-success-${i}` }));
  const failedData = failedRows.map((row, i) => ({ ...row, key: `data-failed-${i}` }));
  const data: (IOnboardingUploadRow & { key: string })[] = [...failedData, ...successData];

  const hasFailedRows = failedRows.length > 0;

  const tableHeaders = useMemo(() => {
    // Extra fields always appended beyond whatever the server sends in headerMapping.
    const extraFields = ",userId:User ID,name:Name,currentStatus:Current Status,alreadyCreated:Already Created";
    // Errors column only when there are failed rows.
    const errorsField = hasFailedRows ? ",errors:Errors" : "";

    if (uploadResult?.headerMapping) {
      return generateHeaders(uploadResult.headerMapping + extraFields + errorsField) || {};
    }
    const base: Record<string, string> = { ...DEFAULT_HEADERS };
    if (hasFailedRows) base.errors = "Errors";
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadResult, hasFailedRows]);

  const columns: ColumnsType<IOnboardingUploadRow & { key: string }> = Object.keys(tableHeaders).map(
    (key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: any, record: IOnboardingUploadRow & { key: string }) => {
        if (key === "errors") {
          return <>{record.errors?.map((e) => e.message).join(", ")}</>;
        }
        if (typeof text === "boolean") return <>{text ? "Yes" : "No"}</>;
        if (text === null || text === undefined) return <>-</>;
        return <>{text}</>;
      },
    })
  );

  const addBorderToErrorRows = (record: IOnboardingUploadRow & { key: string }) =>
    record.errors && record.errors.length ? "error-row" : "";

  const showTable = !!data.length;
  const canSave = showTable && !hasErrorRows && !!uploadResult?.key;

  const handleSave = async () => {
    if (!uploadResult?.key) return;
    setSaving(true);
    try {
      await API.post(apis.saveOnboardingDetails, {}, { params: { key: uploadResult.key } });
      message.success({ content: "Onboarding details saved successfully.", key: MESSAGE_KEY });
      setUploadResult({});
      setHasErrorRows(false);
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || error?.message || "Save failed!";
      message.error({ content: errMsg, key: MESSAGE_KEY });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!uploadResult?.key) {
      setUploadResult({});
      setHasErrorRows(false);
      return;
    }
    try {
      await API.post(apis.saveOnboardingDetails, {}, { params: { key: uploadResult.key, cancel: 1 } });
    } catch {
      // best-effort cancel — clear state regardless
    } finally {
      setUploadResult({});
      setHasErrorRows(false);
    }
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Onboarding Details" />
        </Col>
      </Row>
      <Row style={{ marginTop: "1rem" }}>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadOnboardingDetails}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {showTable && (
        <>
          {hasErrorRows && (
            <Row>
              <Col lg={16} xs={24}>
                <Alert
                  type="error"
                  message="Errors in sheet data."
                  description="Please correct errors and upload again."
                  showIcon
                  banner
                />
              </Col>
            </Row>
          )}
          <Row style={{ marginTop: "1rem" }}>
            <Col xs={24}>
              <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowClassName={(record) => addBorderToErrorRows(record)}
                scroll={{ y: 500, x: "100vh" }}
                className="table-with-errors"
                pagination={false}
              />
            </Col>
          </Row>
          {canSave && (
            <Row style={{ marginTop: "1rem", gap: "10px" }}>
              <Col>
                <Button type="primary" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </Col>
              <Col>
                <Button onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

export default UploadOnboardingDetailsPage;
